import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useTenant } from '@/hooks/useTenant';
import { kpis as mockKpis, projects as mockProjects, tasks as mockTasks } from '@/data/mockData';
import type { KPI, Project, Task } from '@/types';

export interface PortfolioHealthSlice {
  name: string;
  value: number;
}

export interface BudgetSeriesPoint {
  name: string;
  budget: number;
  actual: number;
}

export interface VelocitySeriesPoint {
  name: string;
  value: number;
}

interface SyncedProject {
  id: string;
  external_id: string;
  source: string;
  name: string;
  description: string | null;
  health: string;
  progress: number;
  budget: number;
  spent: number;
  start_date: string | null;
  end_date: string | null;
  priority: string;
  category: string | null;
  team_data: unknown[];
  milestones_data: unknown[];
  raw_data: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

interface SyncedKPI {
  id: string;
  source: string;
  title: string;
  value: string;
  change: number;
  trend: string;
  icon: string;
  raw_data: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface SyncedMeeting {
  id: string;
  external_id: string;
  source: string;
  title: string;
  start_time: string;
  end_time: string | null;
  ai_action_items: unknown[] | null;
  ai_decisions: unknown[] | null;
  created_at: string;
  updated_at: string;
}

export interface SyncedTask {
  id: string;
  external_id: string;
  source: string;
  project_external_id: string | null;
  title: string;
  description: string | null;
  status: string;
  priority: string;
  assignee: string | null;
  due_date: string | null;
  tags: string[];
  raw_data: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

// Transform synced project to app format
function transformProject(syncedProject: SyncedProject): Project {
  return {
    id: syncedProject.external_id,
    name: syncedProject.name,
    description: syncedProject.description || '',
    health: (syncedProject.health as 'on-track' | 'at-risk' | 'critical') || 'on-track',
    progress: syncedProject.progress || 0,
    budget: syncedProject.budget || 0,
    spent: syncedProject.spent || 0,
    team: (syncedProject.team_data || []).map((member: unknown, idx: number) => {
      const m = member as Record<string, unknown>;
      return {
        id: String(m.id || idx),
        name: String(m.name || 'Unknown'),
        role: String(m.role || 'Team Member'),
        avatar: String(m.avatar || ''),
        allocation: Number(m.allocation || 100),
      };
    }),
    milestones: (syncedProject.milestones_data || []).map((ms: unknown, idx: number) => {
      const m = ms as Record<string, unknown>;
      return {
        id: String(m.id || idx),
        name: String(m.name || 'Milestone'),
        startDate: String(m.startDate || m.start_date || new Date().toISOString()),
        endDate: String(m.endDate || m.end_date || new Date().toISOString()),
        progress: Number(m.progress || 0),
        status: (m.status as 'completed' | 'in-progress' | 'upcoming' | 'delayed') || 'upcoming',
      };
    }),
    startDate: syncedProject.start_date || new Date().toISOString(),
    endDate: syncedProject.end_date || new Date().toISOString(),
    priority: (syncedProject.priority as 'critical' | 'high' | 'medium' | 'low') || 'medium',
    category: syncedProject.category || 'General',
    source: syncedProject.source,
    externalId: syncedProject.external_id,
    syncedAt: syncedProject.updated_at,
    syncRowId: syncedProject.id,
  };
}

// Transform synced KPI to app format
function transformKPI(syncedKPI: SyncedKPI, idx: number): KPI {
  return {
    id: syncedKPI.id || String(idx),
    title: syncedKPI.title,
    value: syncedKPI.value,
    change: syncedKPI.change || 0,
    trend: (syncedKPI.trend as 'up' | 'down' | 'stable') || 'stable',
    icon: syncedKPI.icon || 'activity',
  };
}

// Transform synced task to app format
export function transformTask(syncedTask: SyncedTask): Task {
  return {
    id: syncedTask.external_id,
    title: syncedTask.title,
    description: syncedTask.description || '',
    status: (syncedTask.status as 'backlog' | 'todo' | 'in-progress' | 'review' | 'done') || 'todo',
    priority: (syncedTask.priority as 'critical' | 'high' | 'medium' | 'low') || 'medium',
    assignee: syncedTask.assignee || 'Unassigned',
    dueDate: syncedTask.due_date || new Date().toISOString().split('T')[0],
    project: syncedTask.project_external_id || 'Unknown Project',
    tags: syncedTask.tags || [],
  };
}

export function useSyncedProjects(source?: string) {
  return useQuery({
    queryKey: ['synced-projects', source],
    queryFn: async () => {
      let query = supabase
        .from('projects_sync')
        .select('*')
        .order('updated_at', { ascending: false });

      if (source) {
        query = query.eq('source', source);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching synced projects:', error);
        return null;
      }

      return data as SyncedProject[];
    },
    staleTime: 30000, // 30 seconds
  });
}

export function useSyncedKPIs(source?: string) {
  return useQuery({
    queryKey: ['synced-kpis', source],
    queryFn: async () => {
      let query = supabase
        .from('kpis_sync')
        .select('*')
        .order('created_at', { ascending: true });

      if (source) {
        query = query.eq('source', source);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching synced KPIs:', error);
        return null;
      }

      return data as SyncedKPI[];
    },
    staleTime: 30000,
  });
}

export function useSyncedTasks(source?: string, projectExternalId?: string) {
  return useQuery({
    queryKey: ['synced-tasks', source, projectExternalId],
    queryFn: async () => {
      let query = supabase
        .from('tasks_sync')
        .select('*')
        .order('updated_at', { ascending: false });

      if (source) {
        query = query.eq('source', source);
      }

      if (projectExternalId) {
        query = query.eq('project_external_id', projectExternalId);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching synced tasks:', error);
        return null;
      }

      return data as SyncedTask[];
    },
    staleTime: 30000,
  });
}

export function useSyncedMeetings(startIso?: string, endIso?: string) {
  return useQuery({
    queryKey: ['synced-meetings', startIso, endIso],
    queryFn: async () => {
      let query = supabase
        .from('meetings_sync')
        .select('id, external_id, source, title, start_time, end_time, ai_action_items, ai_decisions, created_at, updated_at')
        .order('start_time', { ascending: false });

      if (startIso) query = query.gte('start_time', startIso);
      if (endIso) query = query.lte('start_time', endIso);

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching synced meetings:', error);
        return null;
      }

      return data as SyncedMeeting[];
    },
    staleTime: 30000,
  });
}

const HEALTH_LABELS: Record<string, string> = {
  'on-track': 'On Track',
  'at-risk': 'At Risk',
  critical: 'Critical',
};

function computePortfolioHealth(projects: Project[]): PortfolioHealthSlice[] {
  const counts: Record<string, number> = { 'on-track': 0, 'at-risk': 0, critical: 0 };
  for (const p of projects) {
    counts[p.health] = (counts[p.health] ?? 0) + 1;
  }
  return Object.entries(counts).map(([health, value]) => ({ name: HEALTH_LABELS[health] ?? health, value }));
}

function computeBudgetSeries(projects: Project[]): BudgetSeriesPoint[] {
  return projects.map((p) => ({ name: p.name, budget: p.budget, actual: p.spent }));
}

function computeVelocitySeries(tasks: Task[]): VelocitySeriesPoint[] {
  const completed = tasks.filter((t) => t.status === 'done' && t.dueDate);
  const byWeek = new Map<string, number>();

  for (const t of completed) {
    const d = new Date(t.dueDate);
    if (Number.isNaN(d.getTime())) continue;
    // Bucket by ISO week-start (Monday) so counts are stable regardless of exact day.
    const weekStart = new Date(d);
    const day = (weekStart.getDay() + 6) % 7; // 0 = Monday
    weekStart.setDate(weekStart.getDate() - day);
    const key = weekStart.toISOString().split('T')[0];
    byWeek.set(key, (byWeek.get(key) ?? 0) + 1);
  }

  return Array.from(byWeek.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .slice(-8)
    .map(([weekStart, value]) => ({
      name: new Date(weekStart).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
      value,
    }));
}

// Combined hook that returns synced data, falling back to mock data only for the demo tenant.
export function useDashboardData() {
  const { isDemoTenant, demoMockDataEnabled } = useTenant();
  const showMock = isDemoTenant && demoMockDataEnabled;
  const { data: syncedProjects, isLoading: projectsLoading } = useSyncedProjects();
  const { data: syncedKPIs, isLoading: kpisLoading } = useSyncedKPIs();
  const { data: syncedTasks, isLoading: tasksLoading } = useSyncedTasks();

  const isLoading = projectsLoading || kpisLoading || tasksLoading;

  const projects: Project[] = syncedProjects?.length
    ? syncedProjects.map(transformProject)
    : showMock ? mockProjects : [];

  const kpis: KPI[] = syncedKPIs?.length
    ? syncedKPIs.map(transformKPI)
    : showMock ? mockKpis : [];

  const tasks: Task[] = syncedTasks?.length
    ? syncedTasks.map(transformTask)
    : showMock ? mockTasks : [];

  const hasSyncedData = Boolean(syncedProjects?.length || syncedKPIs?.length || syncedTasks?.length);

  return {
    projects,
    kpis,
    tasks,
    isLoading,
    hasSyncedData,
    syncedProjectsCount: syncedProjects?.length || 0,
    syncedKPIsCount: syncedKPIs?.length || 0,
    syncedTasksCount: syncedTasks?.length || 0,
    portfolioHealthData: computePortfolioHealth(projects),
    budgetSeries: computeBudgetSeries(projects),
    velocitySeries: computeVelocitySeries(tasks),
  };
}
